import { useState, useEffect } from 'react';

interface TextPersonalizationProps {
  designId: number;
  onComplete: (text: string) => void;
  onBack: () => void;
}

const designStyles = {
  1: { bg: 'from-purple-900 via-pink-900 to-purple-900', textColor: 'text-pink-300' },
  2: { bg: 'from-pink-100 via-rose-100 to-pink-50', textColor: 'text-gray-800' },
  3: { bg: 'from-yellow-300 via-orange-300 to-pink-300', textColor: 'text-gray-900' },
  4: { bg: 'from-indigo-500 via-purple-500 to-pink-500', textColor: 'text-white' },
  5: { bg: 'from-blue-200 via-purple-200 to-pink-200', textColor: 'text-gray-800' }
};

const emojis = ['❤️', '💕', '💖', '💗', '💓', '💝', '💘', '💞', '🥰', '😍', '😘', '🌹', '🌺', '✨', '💫', '⭐', '🎀', '🦋', '🌈', '☁️'];

const templates = [
  'Ты особенный человек для меня ❤️',
  'С тобой мир становится ярче ✨',
  'Спасибо, что ты есть 💕',
  'Ты делаешь меня счастливым 🥰',
  'Думаю о тебе каждый день 💭'
];

export default function TextPersonalization({ designId, onComplete, onBack }: TextPersonalizationProps) {
  const [text, setText] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  const style = designStyles[designId as keyof typeof designStyles];
  const isValid = text.trim().length > 0;

  useEffect(() => {
    setCharCount(text.length);
    setIsTyping(true);
    const timer = setTimeout(() => setIsTyping(false), 300);
    return () => clearTimeout(timer);
  }, [text]);

  const handleTextChange = (value: string) => {
    if (value.length <= 200) {
      setText(value);
    }
  };

  const handleEmojiClick = (emoji: string) => {
    if (text.length < 200) {
      setText(text + emoji);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setText(suggestion);
  };

  const handleContinue = () => {
    if (text.trim().length > 0) {
      onComplete(text);
    }
  };

  const handleSubmit = () => {
    if (text.trim().length > 0) {
      onComplete(text);
    }
  };

  return (
    <div className="animate-slide-in">
      <div className="text-center mb-8">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#2AABEE] transition-colors duration-300 mb-6 font-medium cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
          Назад к дизайнам
        </button>
        <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
          Напиши своё послание
        </h2>
        <p className="text-lg text-gray-400">
          Выбери готовую фразу или напиши что-то своё ✨
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Text input section */}
          <div className="space-y-6">
            <div className="bg-[#232E3C] rounded-3xl shadow-xl p-8 border border-[#2C3847]">
              <label className="block text-lg font-bold text-white mb-4">
                Твоё послание ({text.length}/200)
              </label>
              
              <textarea
                value={text}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder="Напиши что-то особенное..."
                className="w-full h-40 px-6 py-4 bg-[#17212b] border-2 border-[#2C3847] rounded-2xl focus:border-[#2AABEE] focus:ring-4 focus:ring-[#2AABEE]/20 outline-none transition-all duration-300 text-base resize-none text-white placeholder-gray-500"
                maxLength={200}
              />

              {/* Character counter */}
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-sm ${text.length > 180 ? 'text-rose-400' : 'text-gray-500'}`}>
                  {text.length > 180 && '⚠️ '} Осталось символов: {200 - text.length}
                </span>
                {text.length >= 10 && (
                  <span className="text-sm text-green-400 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    Отлично!
                  </span>
                )}
              </div>

              {/* Emoji picker */}
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-400 mb-3">Добавь эмодзи:</p>
                <div className="flex flex-wrap gap-2">
                  {emojis.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleEmojiClick(emoji)}
                      className="w-12 h-12 bg-[#17212b] hover:bg-[#2C3847] rounded-xl transition-all duration-200 text-2xl hover:scale-110 cursor-pointer border border-[#2C3847] hover:border-[#2AABEE]"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Template phrases */}
            <div className="bg-[#232E3C] rounded-3xl shadow-xl p-8 border border-[#2C3847]">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-flash-fill text-yellow-400"></i>
                Готовые фразы
              </h3>
              
              <div className="space-y-3">
                {templates.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => setText(template)}
                    className="w-full text-left px-5 py-4 bg-[#17212b] hover:bg-[#2C3847] rounded-2xl transition-all duration-200 text-sm text-gray-300 hover:text-white cursor-pointer border border-[#2C3847] hover:border-[#2AABEE] whitespace-nowrap overflow-hidden text-ellipsis"
                  >
                    {template}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleContinue}
              disabled={!isValid}
              className={`w-full py-5 rounded-2xl font-bold text-lg transition-all duration-300 whitespace-nowrap ${
                isValid
                  ? 'bg-gradient-to-r from-[#2AABEE] to-[#229ED9] text-white hover:shadow-2xl hover:scale-105 cursor-pointer'
                  : 'bg-[#2C3847] text-gray-500 cursor-not-allowed'
              }`}
            >
              Продолжить →
            </button>
          </div>

          {/* Live preview */}
          <div className="lg:sticky lg:top-8 h-fit">
            <div className="bg-[#232E3C] rounded-3xl shadow-xl p-8 border border-[#2C3847]">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <i className="ri-eye-fill text-[#2AABEE]"></i>
                Превью валентинки
              </h3>
              
              <div className={`relative w-full aspect-[4/5] bg-gradient-to-br ${style.bg} rounded-3xl overflow-hidden shadow-2xl`}>
                {/* Animated hearts */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-10 left-10 animate-float">
                    <i className="ri-heart-fill text-4xl text-pink-400 opacity-40"></i>
                  </div>
                  <div className="absolute top-20 right-16 animate-float-delayed">
                    <i className="ri-heart-fill text-3xl text-rose-400 opacity-40"></i>
                  </div>
                  <div className="absolute bottom-20 left-20 animate-float">
                    <i className="ri-heart-fill text-5xl text-pink-300 opacity-40"></i>
                  </div>
                </div>

                {/* Text content */}
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <p className={`text-2xl font-medium text-center leading-relaxed ${style.textColor} transition-all duration-300`}>
                    {text || 'Твоё послание появится здесь...'}
                  </p>
                </div>

                {/* Sparkles */}
                <div className="absolute top-8 right-8 w-2 h-2 bg-white rounded-full animate-ping"></div>
                <div className="absolute bottom-12 left-12 w-3 h-3 bg-white rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 bg-[#17212b] rounded-2xl border border-[#2C3847]">
                <p className="text-sm text-gray-400 flex items-start gap-2">
                  <i className="ri-information-fill text-[#2AABEE] mt-0.5"></i>
                  <span>Совет: Используй эмодзи и будь искренним — это делает валентинку особенной! 💕</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}